import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-hot-toast";
import { FaCheck, FaTimes, FaCheckCircle } from "react-icons/fa";
import Loader from "../../../../components/loader";
import api from "../../../../services/api";
import { TRAINING_TEST_QUESTIONS, calculateEvaluationScoresFromTest } from "./test-questions";

export default function TraineeTest() {
  const { id } = useParams();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [testResults, setTestResults] = useState(null);

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      setLoading(true);
      const userRes = await api.get(`/user/${id}`);
      if (!userRes.ok) {
        toast.error("User not found");
        return;
      }
      setUser(userRes.data);
      
      // Load existing test answers if any
      if (userRes.data.training_test_answers) {
        setAnswers(userRes.data.training_test_answers);
      }
      
      // Check if test already submitted
      if (userRes.data.training_test_completed_at) {
        setSubmitted(true);
        if (userRes.data.training_test_results) {
          setTestResults(userRes.data.training_test_results);
        }
        if (userRes.data.training_test_answers) {
          setAnswers(userRes.data.training_test_answers);
        }
      }
    } catch (error) {
      console.log(error);
      toast.error("Error loading data");
    } finally {
      setLoading(false);
    }
  };

  const handleAnswer = (questionId, answerIndex) => {
    if (submitted) {
      toast.error("Test already submitted. Contact your manager if you need to retake.");
      return;
    }
    setAnswers({ ...answers, [questionId]: answerIndex });
  };

  const saveProgress = async () => {
    try {
      await api.put(`/user/${id}`, {
        training_test_answers: answers,
      });
    } catch (error) {
      console.log("Error saving progress:", error);
    }
  };

  useEffect(() => {
    // Auto-save progress every 30 seconds
    if (!submitted && Object.keys(answers).length > 0) {
      const timer = setTimeout(saveProgress, 30000);
      return () => clearTimeout(timer);
    }
  }, [answers, submitted]);

  const submitTest = async () => {
    const unansweredCount = TRAINING_TEST_QUESTIONS.length - Object.keys(answers).length;
    
    if (unansweredCount > 0) {
      if (!confirm(`You have ${unansweredCount} unanswered questions. Submit anyway?`)) {
        return;
      }
    }

    try {
      // Calculate results
      const results = {
        totalQuestions: TRAINING_TEST_QUESTIONS.length,
        answered: Object.keys(answers).length,
        correct: 0,
        incorrect: 0,
        unanswered: 0,
        byCriterion: {},
        byPillar: { 
          deliverables: { correct: 0, total: 0 },
          skills: { correct: 0, total: 0 },
          culture: { correct: 0, total: 0 }
        },
      };

      TRAINING_TEST_QUESTIONS.forEach((q) => {
        const answer = answers[q.id];
        
        // Track total questions per pillar
        results.byPillar[q.pillar].total++;
        
        if (answer === undefined) {
          results.unanswered++;
          return;
        }

        if (answer === q.correctAnswer) {
          results.correct++;
          results.byPillar[q.pillar].correct++;
        } else {
          results.incorrect++;
        }

        // Track by criterion
        if (!results.byCriterion[q.criterion]) {
          results.byCriterion[q.criterion] = { correct: 0, total: 0 };
        }
        results.byCriterion[q.criterion].total++;
        if (answer === q.correctAnswer) {
          results.byCriterion[q.criterion].correct++;
        }
      });

      // Calculate evaluation scores from test
      const evaluationScores = calculateEvaluationScoresFromTest(answers);

      const { ok } = await api.put(`/user/${id}`, {
        training_test_answers: answers,
        training_test_results: results,
        training_test_completed_at: new Date().toISOString(),
        training_test_evaluation_scores: evaluationScores,
      });

      if (!ok) return toast.error("Failed to submit test");
      
      setSubmitted(true);
      setTestResults(results);
      toast.success("Test submitted successfully! Your manager will review it.");
    } catch (error) {
      console.log(error);
      toast.error("Error submitting test");
    }
  };

  if (loading) return <Loader />;
  if (!user) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">User not found</h1>
        <p className="text-gray-600">Please check your link and try again.</p>
      </div>
    </div>
  );

  const currentQuestion = TRAINING_TEST_QUESTIONS[currentQuestionIndex];
  const totalQuestions = TRAINING_TEST_QUESTIONS.length;
  const answeredCount = Object.keys(answers).length;
  const progress = (answeredCount / totalQuestions) * 100;

  // Group questions by pillar for navigation
  const questionsByPillar = {
    deliverables: TRAINING_TEST_QUESTIONS.filter(q => q.pillar === "deliverables"),
    skills: TRAINING_TEST_QUESTIONS.filter(q => q.pillar === "skills"),
    culture: TRAINING_TEST_QUESTIONS.filter(q => q.pillar === "culture"),
  };

  if (submitted && testResults) {
    // Show simple results
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-6">
        <div className="max-w-3xl mx-auto">
          <div className="bg-white rounded-xl shadow-lg p-8 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <FaCheckCircle className="text-green-600 text-4xl" />
            </div>
            
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Test Completed!</h1>
            <p className="text-gray-600 mb-8">Your manager will review your results and provide feedback.</p>

            <div className="grid grid-cols-3 gap-4 mb-8">
              <div className="bg-blue-50 rounded-lg p-4">
                <div className="text-3xl font-bold text-blue-600">{testResults.correct}</div>
                <div className="text-sm text-gray-600">Correct</div>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="text-3xl font-bold text-gray-900">{testResults.totalQuestions}</div>
                <div className="text-sm text-gray-600">Total Questions</div>
              </div>
              <div className="bg-green-50 rounded-lg p-4">
                <div className="text-3xl font-bold text-green-600">
                  {Math.round((testResults.correct / testResults.totalQuestions) * 100)}%
                </div>
                <div className="text-sm text-gray-600">Score</div>
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-6 text-left">
              <h3 className="font-semibold text-gray-900 mb-3">What's Next?</h3>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-start gap-2">
                  <span className="text-blue-600 mt-1">•</span>
                  <span>Your manager will review your test results along with your practical work</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-600 mt-1">•</span>
                  <span>They will provide feedback on your training performance</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-600 mt-1">•</span>
                  <span>Continue focusing on your training tasks and applying feedback</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-t-xl shadow-lg p-6 border-b border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Training Evaluation Test</h1>
              <p className="text-sm text-gray-600 mt-1">Welcome {user.name}! Complete all questions and submit when ready.</p>
            </div>
            <div className="text-right">
              <div className="text-sm text-gray-600">Progress</div>
              <div className="text-2xl font-bold text-blue-600">{answeredCount}/{totalQuestions}</div>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div
              className="bg-blue-600 h-3 rounded-full transition-all"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Question Card */}
        <div className="bg-white shadow-lg p-8">
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-medium text-gray-500">
                Question {currentQuestionIndex + 1} of {totalQuestions}
              </span>
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                currentQuestion.pillar === "deliverables" ? "bg-amber-100 text-amber-700" :
                currentQuestion.pillar === "skills" ? "bg-blue-100 text-blue-700" :
                "bg-teal-100 text-teal-700"
              }`}>
                {currentQuestion.pillar}
              </span>
            </div>

            <h2 className="text-xl font-semibold text-gray-900 mb-6">
              {currentQuestion.question}
            </h2>

            <div className="space-y-3">
              {currentQuestion.options.map((option, idx) => {
                const isSelected = answers[currentQuestion.id] === idx;
                return (
                  <button
                    key={idx}
                    onClick={() => handleAnswer(currentQuestion.id, idx)}
                    className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                      isSelected
                        ? "border-blue-600 bg-blue-50"
                        : "border-gray-200 bg-white hover:border-blue-300 hover:bg-gray-50"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                        isSelected ? "border-blue-600 bg-blue-600" : "border-gray-300"
                      }`}>
                        {isSelected && <FaCheck className="text-white text-xs" />}
                      </div>
                      <span className={`text-sm ${isSelected ? "font-medium text-gray-900" : "text-gray-700"}`}>
                        {option}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between pt-6 border-t border-gray-200">
            <button
              onClick={() => setCurrentQuestionIndex(Math.max(0, currentQuestionIndex - 1))}
              disabled={currentQuestionIndex === 0}
              className="px-4 py-2 text-gray-600 hover:text-gray-900 disabled:opacity-30 disabled:cursor-not-allowed"
            >
              ← Previous
            </button>

            <div className="flex items-center gap-2">
              {TRAINING_TEST_QUESTIONS.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentQuestionIndex(idx)}
                  className={`w-2 h-2 rounded-full transition-all ${
                    idx === currentQuestionIndex
                      ? "bg-blue-600 w-8"
                      : answers[TRAINING_TEST_QUESTIONS[idx].id] !== undefined
                      ? "bg-green-500"
                      : "bg-gray-300"
                  }`}
                  title={`Question ${idx + 1}`}
                />
              ))}
            </div>

            {currentQuestionIndex < totalQuestions - 1 ? (
              <button
                onClick={() => setCurrentQuestionIndex(currentQuestionIndex + 1)}
                className="px-4 py-2 text-blue-600 hover:text-blue-700 font-medium"
              >
                Next →
              </button>
            ) : (
              <button
                onClick={submitTest}
                className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium"
              >
                Submit Test
              </button>
            )}
          </div>
        </div>

        {/* Quick Navigation */}
        <div className="bg-white rounded-b-xl shadow-lg p-6 mt-0">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Quick Navigation</h3>
          <div className="grid grid-cols-3 gap-3">
            {Object.entries(questionsByPillar).map(([pillar, questions]) => {
              const answeredInPillar = questions.filter(q => answers[q.id] !== undefined).length;
              return (
                <div key={pillar} className="bg-gray-50 rounded-lg p-3">
                  <div className="text-xs font-medium text-gray-600 capitalize mb-2">{pillar}</div>
                  <div className="text-lg font-bold text-gray-900">
                    {answeredInPillar}/{questions.length}
                  </div>
                  <div className="flex gap-1 mt-2 flex-wrap">
                    {questions.map((q) => {
                      const qIndex = TRAINING_TEST_QUESTIONS.findIndex(tq => tq.id === q.id);
                      const isAnswered = answers[q.id] !== undefined;
                      return (
                        <button
                          key={q.id}
                          onClick={() => setCurrentQuestionIndex(qIndex)}
                          className={`w-6 h-6 text-xs rounded ${
                            qIndex === currentQuestionIndex
                              ? "bg-blue-600 text-white font-bold"
                              : isAnswered
                              ? "bg-green-500 text-white"
                              : "bg-gray-200 text-gray-600"
                          }`}
                        >
                          {qIndex + 1}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
